import { AHSeparator } from "arkh";
import { FlatList, Platform, StyleSheet } from "react-native";
import React, { useCallback, useEffect, useImperativeHandle, useRef, useMemo } from "react";
import type { ViewToken } from "react-native";

import { graphql, useFragment } from "react-relay";
import { WidgetEmployeeRoleOverviewContent_widget$key } from "./__generated__/WidgetEmployeeRoleOverviewContent_widget.graphql";
import { WidgetEmployeeRoleOverviewContent_widgetSubscription } from "./__generated__/WidgetEmployeeRoleOverviewContent_widgetSubscription.graphql";
import {
  WidgetEmployeeRoleOverviewListItem,
  type WidgetEmployeeRoleOverviewListItemPressData,
} from "./WidgetEmployeeRoleOverviewListItem";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";

const { containerStyle } = StyleSheet.create({
  containerStyle: { paddingBottom: 10 },
});

export type WidgetEmployeeRoleOverviewContentRef = {
  scrollTo: (itemId: string) => void;
};

export const WidgetEmployeeRoleOverviewContent = (props: {
  widget: WidgetEmployeeRoleOverviewContent_widget$key;
  ref?: React.RefObject<WidgetEmployeeRoleOverviewContentRef | null>;
  interactive?: boolean;
  isFocused?: boolean;
  highlightedItemId?: string;
  scrollToItemId?: string;
  setScroll?: (itemId: string) => void;
  onPressRole?(data: WidgetEmployeeRoleOverviewListItemPressData): void;
}) => {
  const widget = useFragment(
    graphql`
      fragment WidgetEmployeeRoleOverviewContent_widget on WidgetEmployeeRoleOverview {
        roleContent: content {
          ... on WidgetEmployeeRoleOverviewContentListMedium {
            subscriptionKey
            roles {
              id
              ...WidgetEmployeeRoleOverviewListItem_role
            }
          }
        }
      }
    `,
    props.widget
  );

  useSubscriptionWithSkip<WidgetEmployeeRoleOverviewContent_widgetSubscription>(
    useMemo(
      () => ({
        variables: {
          subscriptionKey: widget?.roleContent?.subscriptionKey ?? "",
        },
        subscription: graphql`
          subscription WidgetEmployeeRoleOverviewContent_widgetSubscription(
            $subscriptionKey: String!
          ) {
            widgetEmployeeRoleOverviewContentChanged(
              subscriptionKey: $subscriptionKey
            ) {
              ... on WidgetEmployeeRoleOverviewContentListMedium {
                subscriptionKey
                roles {
                  id
                  ...WidgetEmployeeRoleOverviewListItem_role
                }
              }
            }
          }
        `,
      }),
      [widget?.roleContent?.subscriptionKey]
    ),
    {
      skip: props.interactive === false,
    }
  );

  const roles = useMemo(
    () => (widget?.roleContent?.roles ?? []).filter((role) => role?.id),
    [widget?.roleContent?.roles]
  );

  const renderRow = ({ item }: { item: (typeof roles)[number] }) => (
    <WidgetEmployeeRoleOverviewListItem
      role={item}
      onPress={props.onPressRole}
      isFocused={item.id === props.highlightedItemId}
    />
  );

  const keyExtractor = (item: (typeof roles)[0]) => item.id;

  const flatListRef = useRef<FlatList>(null);
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const lastScrollId = useRef<string | null>(null);
  const isFocusedRef = useRef(props.isFocused);
  isFocusedRef.current = props.isFocused;
  const setScrollRef = useRef(props.setScroll);
  setScrollRef.current = props.setScroll;
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
    };
  }, []);

  useImperativeHandle(
    props.ref,
    () => ({
      scrollTo(itemId: string) {
        const index = rolesRef.current.findIndex((role) => role.id === itemId);
        if (index !== -1) {
          flatListRef.current?.scrollToIndex({
            index,
            animated: true,
            viewPosition: 0,
          });
        }
      },
    }),
    []
  );

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (!isFocusedRef.current || !setScrollRef.current) return;
      const first = viewableItems[0];
      if (first?.item?.id && lastScrollId.current !== first.item.id) {
        lastScrollId.current = first.item.id;
        if (scrollTimer.current) clearTimeout(scrollTimer.current);
        scrollTimer.current = setTimeout(() => {
          setScrollRef.current?.(lastScrollId.current!);
        }, 150);
      }
    },
    []
  );

  const viewabilityConfig = useMemo(
    () => ({ itemVisiblePercentThreshold: 50 }),
    []
  );

  const onScrollToIndexFailed = useCallback(
    (info: { index: number; averageItemLength: number }) => {
      flatListRef.current?.scrollToOffset({
        offset: info.averageItemLength * info.index,
        animated: true,
      });
    },
    []
  );

  const scrollTarget = props.scrollToItemId ?? props.highlightedItemId;
  useEffect(() => {
    if (scrollTarget) {
      const index = roles.findIndex((role) => role.id === scrollTarget);
      if (index !== -1) {
        flatListRef.current?.scrollToIndex({
          index,
          animated: true,
          viewPosition: 0.5,
        });
      }
    }
  }, [scrollTarget, roles]);

  return (
    <FlatList
      ref={flatListRef}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      ItemSeparatorComponent={AHSeparator}
      keyExtractor={keyExtractor}
      data={roles}
      renderItem={renderRow}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={viewabilityConfig}
      scrollEnabled={!(props.interactive === false)}
      onScrollToIndexFailed={onScrollToIndexFailed}
      contentContainerStyle={containerStyle}
    />
  );
};
